import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Creators as TaskCreators } from 'store/ducks/task';
import { Creators as ChatCreators } from 'store/ducks/chat';

import { validateUserFields } from 'triider-components/src/lib/utils/userValidations';

import Modal from 'components/Modal';

import { PaymentMethods } from 'config/shared';
import PaymentInfoStep from '../AuthorizeModal/PaymentInfoStep';

import DateStep from './DateStep';
import FinalStep from './FinalStep';
import PaymentMethodSelector from './PaymentMethodSelector';
import CheckoutLink from './CheckoutLink';
import SEGMENTS from '../../../../../helpers/segment';

const HireModal = ({
  hireModalIsOpen,
  setHireModalIsOpen,
  taskDetail,
  taskQuoteProDetail,
  selectedTaskHired,
  triiderUser,
  isLoading,
  loadingVisible,
  checkoutParams,
  _hireTask,
  _schedulingRequest,
  _getTaskDetail,
  _initChat,
  _removeChats,
  _taskPaymentInfo,
}) => {
  const [step, setStep] = useState(1);
  const IMOB_SEGMENT = triiderUser.company && (triiderUser.company.segment_id === null || triiderUser.company.segment_id === SEGMENTS.IMOBILIARIA);

  const [stepInfo, setStepInfo] = useState(IMOB_SEGMENT ? 0 : 1);
  const [schedulingDate, setSchedulingDate] = useState(
    taskDetail.schedule_date ? new Date(taskDetail.schedule_date) : new Date(),
  );
  const [billingType, setBillingType] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [fields, setFields] = useState({});
  const [checkoutFields, setCheckoutFields] = useState({});
  const [checkoutValidations, setCheckoutValidations] = useState({});
  const [paymentMethodSelected, setPaymentMethod] = useState(
    PaymentMethods.DOC,
  );
  const checkout_token = checkoutParams.checkout_token || taskDetail.checkout_token;

  useEffect(() => {
    if (selectedTaskHired) {
      setStep(3);
    } else if (
      taskDetail.responsibility_bucket_id !== 2
      || triiderUser.company.use_billing_company_info
    ) {
      setStep(2);
    } else if (taskDetail.billing_info) {
      const {
        from_type, name, email, cpf, cnpj,
      } = taskDetail.billing_info;
      setStep(2);
      setIsEditing(true);
      setBillingType(from_type);
      setFields({
        name,
        email,
        cpf,
        cnpj,
      });
    } else {
      setStep(1);
      setBillingType(IMOB_SEGMENT ? 'Imobiliária' : 'Empresa');
      setFields({
        name: triiderUser.company.name || '',
        email: triiderUser.user.email || '',
        cnpj: triiderUser.company.cnpj || '',
        cpf: '',
      });
    }
  }, [selectedTaskHired, taskDetail]);

  const handleStep = () => {
    switch (step) {
      case 1: {
        if (paymentMethodSelected === PaymentMethods.CREDIT_CARD) {
          return (
            <CheckoutLink
              fields={checkoutFields}
              setFields={setCheckoutFields}
              validations={checkoutValidations}
              setValidations={setCheckoutValidations}
              checkout_token={checkout_token}
            />
          );
        }
        return (
          <PaymentInfoStep
            isLoading={isLoading}
            taskDetail={taskDetail}
            triiderUser={triiderUser}
            billingType={billingType}
            fields={fields}
            setBillingType={setBillingType}
            setFields={setFields}
            stepInfo={stepInfo}
          />
        );
      }
      case 2:
        return (
          <DateStep
            setStep={setStep}
            schedulingDate={taskDetail.schedule_date}
            taskDetailResponsibilitId={taskDetail.responsibility_bucket_id}
            useBillingCompanyInfo={triiderUser.company.use_billing_company_info}
            setSchedulingDate={setSchedulingDate}
          />
        );
      case 3:
        return (
          <>
            <FinalStep quote={taskQuoteProDetail} />
            {paymentMethodSelected === PaymentMethods.CREDIT_CARD && (
              <CheckoutLink
                fields={checkoutFields}
                setFields={setCheckoutFields}
                validations={checkoutValidations}
                setValidations={setCheckoutValidations}
                checkout_token={checkout_token}
              />
            )}
          </>
        );
      default:
        return <div />;
    }
  };

  const schedulingRequest = async (taskId, quoteId) => {
    const data = {
      taskId,
      scheduleDate: schedulingDate,
      quoteId,
      message: `${new Date(schedulingDate).toLocaleDateString('pt-BR')}`,
    };
    await _schedulingRequest(data);
  };

  const handleClick = () => {
    switch (step) {
      case 1: {
        if (paymentMethodSelected === PaymentMethods.CREDIT_CARD) {
          if (checkout_token) {
            return closeModal();
          }

          const v = {};
          Object.entries(checkoutFields).forEach(([key, value]) => {
            Object.assign(v, { [key]: validateUserFields(key, value) });
          });

          setCheckoutValidations(v);

          if (Object.values(v).filter(o => !!o).length > 0) {
            return;
          }

          return _hireTask({
            generateCheckoutLink: true,
            user: checkoutFields,
            taskId: taskDetail.id,
            quoteId: taskQuoteProDetail.id,
          });
        }

        if (stepInfo === 0) {
          setStepInfo(1);
          return;
        }
        return _taskPaymentInfo({
          ...fields,
          task_id: taskDetail.id,
          from_type: billingType,
          address_id: taskDetail.address_id,
          update: isEditing,
          userId: triiderUser.user.id,
          paymentMethodSelected,
        });
      }

      case 2:
        const taskId = taskDetail.id;
        const quoteId = taskQuoteProDetail.id;
        if (taskDetail.status !== 'hired') _hireTask({ taskId, quoteId });
        schedulingRequest(taskId, quoteId);
        break;

      default:
        break;
    }
  };

  const closeModal = () => {
    setStepInfo(0);
    setHireModalIsOpen(false);
    _getTaskDetail(taskDetail.id);
    if (taskDetail.quotes && taskDetail.quotes.length > 0) {
      taskDetail.quotes.forEach((q) => {
        if (q.chat_id) _initChat(q.chat_id);
      });
    }
    _removeChats();
  };

  const disableFooterClick = () => {
    if (step === 1) {
      if (paymentMethodSelected === PaymentMethods.CREDIT_CARD && !checkout_token) {
        return loadingVisible || !checkoutFields.email;
      }
      return (
        loadingVisible
        || !fields.name
        || !fields.email
        || (!fields.cpf && !fields.cnpj)
      );
    }
    return loadingVisible;
  };

  const footerText = () => {
    switch (step) {
      case 1: {
        if (paymentMethodSelected === PaymentMethods.CREDIT_CARD) {
          if (!checkout_token && !checkoutFields.validatedEmail) {
            return null;
          }

          return 'Próximo';
        }

        return 'Próximo';
      }
      case 2:
        return 'Contratar';
      case 3:
        return 'Fechar';

      default:
        return 'Próximo';
    }
  };

  return (
    <Modal
      isOpen={hireModalIsOpen}
      toggle={() => setHireModalIsOpen(!hireModalIsOpen)}
      title="CONTRATAR PROFISSIONAL"
      disabled={disableFooterClick()}
      padding={step === 3 ? '0' : '40px 60px'}
      overflow="auto"
      footer={footerText()}
      footerClick={() => (selectedTaskHired ? closeModal() : handleClick())}
    >
      {step === 1 && IMOB_SEGMENT && (
        <PaymentMethodSelector
          paymentMethodSelected={paymentMethodSelected}
          setPaymentMethod={setPaymentMethod}
        />
      )}
      {handleStep(step)}
    </Modal>
  );
};

const mapStateToProps = state => ({
  taskDetail: state.task.taskDetail,
  taskQuoteProDetail: state.task.taskQuoteProDetail,
  chats: state.chat.chats,
  isLoading: state.task.loading,
  selectedTaskHired: state.task.selectedTaskHired,
  checkoutParams: state.task.checkoutParams,
  triiderUser: state.auth.triiderUser,
  loadingVisible: state.ui.loaders.includes(
    'PAYMENT_INFO',
    'SCHEDULING_REQUEST',
    'HIRE_TASK',
    'GENERATE_CHECKOUT_LINK',
  ) || state.ui.loaders.includes('GENERATE_CHECKOUT_LINK'),
});

const mapDispatchToProps = {
  _hireTask: TaskCreators.hireTaskRequest,
  _getTaskDetail: TaskCreators.getTaskDetailRequest,
  _schedulingRequest: TaskCreators.schedulingRequest,
  _initChat: ChatCreators.initChatRequest,
  _removeChats: ChatCreators.removeChats,
  _taskPaymentInfo: TaskCreators.taskPaymentInfoRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(HireModal);
